/**
 * <lazy-hydrate> v2
 *
 * Below-fold section hydration wrapper. Defers rendering of everything inside
 * its child <template> until the element is close to the viewport. Used on
 * sections whose initial cost (product images, Judge.me widget scans, custom-
 * element upgrades, section-rendering fetches) does not need to happen until
 * the user is nearly scrolled to them.
 *
 * Markup contract (server-rendered):
 *   <lazy-hydrate data-root-margin="800px 0px" aria-busy="true">
 *     <div class="lazy-hydrate__skeleton" aria-hidden="true">
 *       <!-- CLS-preserving placeholder boxes (optional) -->
 *     </div>
 *     <template>
 *       <!-- The real section content. Images inside <template> are inert
 *            per HTML spec: browsers do not fetch them, custom elements do
 *            not upgrade, and Judge.me's MutationObserver does not see
 *            preview badges. Everything wakes up only after clone. -->
 *     </template>
 *   </lazy-hydrate>
 *
 * Hydration triggers (any one fires it, all are one-shot):
 *   1. IntersectionObserver, rootMargin from data-root-margin (default 800px)
 *   2. focusin on the host - keyboard/AT users who reach the region focus-
 *      first get content immediately rather than a skeleton
 *   3. window beforeprint - so Ctrl-P before scrolling still prints the
 *      full page, not placeholder boxes
 *   4. no IntersectionObserver support - hydrate immediately
 *
 * On hydrate the template content is cloned into the live tree, the skeleton
 * is removed, aria-busy flips to false, and a bubbling `lazy-hydrated` event
 * fires so ambient listeners can hook in.
 *
 * Judge.me nudge: preview badges (star ratings on product-item cards) are
 * rendered by widget.js on DOM mutation, but widget.js may have finished its
 * initial scan before we injected, or may not have loaded yet (consent
 * gating). We call jdgm.batchWidgetsRendering() when available and dispatch
 * the jdgm-reviews-loaded event as a fallback, with a bounded retry. If
 * Judge.me loads after the retry window (e.g. consent granted late), its own
 * initial scan will find the badges in the live DOM, so this self-heals.
 *
 * Section Rendering API warning for section authors: content fetched via the
 * Section Rendering API is often extracted with div.querySelector(...), which
 * does NOT descend into <template>.content. Sections whose own JS re-fetches
 * them (product-recommendations, recently-viewed-products) must NOT emit this
 * wrapper in the fetch context - gate on recommendations.performed /
 * request.page_type as appropriate.
 *
 * Design mode: this element is not emitted by the server when
 * request.design_mode is true, so merchants see the full section in the
 * theme editor.
 */
(function () {
  if (customElements.get('lazy-hydrate')) return;

  var DEFAULT_ROOT_MARGIN = '800px 0px';
  var JDGM_RETRY_LIMIT = 24;
  var JDGM_RETRY_MS = 250;

  function LazyHydrate() {
    return Reflect.construct(HTMLElement, [], LazyHydrate);
  }
  LazyHydrate.prototype = Object.create(HTMLElement.prototype);
  LazyHydrate.prototype.constructor = LazyHydrate;
  Object.setPrototypeOf(LazyHydrate, HTMLElement);

  LazyHydrate.prototype.connectedCallback = function () {
    if (this._hydrated) return;

    var self = this;

    // Print completeness: hydrate everything before the print snapshot so a
    // user printing without scrolling gets real content, not skeletons.
    this._onBeforePrint = function () { self._hydrate(); };
    window.addEventListener('beforeprint', this._onBeforePrint);

    // Keyboard/AT belt-and-braces: focus landing inside or on the host (e.g.
    // via sequential tabbing or a skip link) hydrates immediately. In
    // practice the IO fires first because focusing scrolls the page, but
    // this removes any ordering dependency.
    this._onFocusIn = function () { self._hydrate(); };
    this.addEventListener('focusin', this._onFocusIn);

    // Fallback: no IntersectionObserver available. Hydrate immediately so
    // content is never hidden from users on unsupported browsers.
    if (!('IntersectionObserver' in window)) {
      this._hydrate();
      return;
    }

    var rootMargin = this.getAttribute('data-root-margin') || DEFAULT_ROOT_MARGIN;
    this._observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          self._hydrate();
          return;
        }
      }
    }, { rootMargin: rootMargin });
    this._observer.observe(this);
  };

  LazyHydrate.prototype.disconnectedCallback = function () {
    this._teardown();
  };

  LazyHydrate.prototype._teardown = function () {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._onBeforePrint) {
      window.removeEventListener('beforeprint', this._onBeforePrint);
      this._onBeforePrint = null;
    }
    if (this._onFocusIn) {
      this.removeEventListener('focusin', this._onFocusIn);
      this._onFocusIn = null;
    }
  };

  /**
   * Public entry point. Lets controls that sit OUTSIDE the host - most
   * importantly tab strips whose panels live inside our <template> - force
   * hydration before their own handlers run. See data-lazy-hydrate-target.
   */
  LazyHydrate.prototype.hydrate = function () {
    this._hydrate();
  };

  LazyHydrate.prototype._hydrate = function () {
    if (this._hydrated) return;

    var tpl = this.querySelector(':scope > template');

    // Parse-timing safety: if this element was upgraded before its children
    // finished parsing (possible for dynamically streamed content), the
    // template may not exist YET. Do not latch _hydrated in that case -
    // retry once the document is ready so content is never permanently
    // stuck inside an unfound template.
    if (!tpl) {
      if (document.readyState === 'loading') {
        var self = this;
        document.addEventListener('DOMContentLoaded', function () { self._hydrate(); }, { once: true });
        return;
      }
      // Document is ready and there is genuinely no template (server chose
      // not to wrap, or content was already hydrated externally). Done.
      this._hydrated = true;
      this._teardown();
      this.setAttribute('aria-busy', 'false');
      return;
    }

    this._hydrated = true;
    this._teardown();

    var skeleton = this.querySelector(':scope > .lazy-hydrate__skeleton');
    var frag = tpl.content.cloneNode(true);

    // Insert the content in place of the template so custom-element upgrades
    // happen in document order.
    tpl.parentNode.insertBefore(frag, tpl);
    tpl.remove();
    if (skeleton) skeleton.remove();

    this.setAttribute('aria-busy', 'false');

    this.dispatchEvent(new CustomEvent('lazy-hydrated', {
      bubbles: true,
      detail: { element: this }
    }));

    this._nudgeJudgeMe(0);
  };

  LazyHydrate.prototype._nudgeJudgeMe = function (attempts) {
    // Only bother when Judge.me content is actually present in this subtree;
    // avoids 6 seconds of no-op event dispatch on sections without reviews.
    if (attempts === 0 && !this.querySelector('.jdgm-prev-badge, .jdgm-preview-badge, [class*="jdgm-"]')) {
      return;
    }

    try {
      if (window.jdgm && typeof window.jdgm.batchWidgetsRendering === 'function') {
        window.jdgm.batchWidgetsRendering();
        return;
      }
    } catch (e) { /* fall through to retry */ }

    try { document.dispatchEvent(new Event('jdgm-reviews-loaded')); } catch (e) { /* noop */ }

    if (attempts < JDGM_RETRY_LIMIT) {
      var self = this;
      window.setTimeout(function () { self._nudgeJudgeMe(attempts + 1); }, JDGM_RETRY_MS);
    }
  };

  customElements.define('lazy-hydrate', LazyHydrate);

  /**
   * data-lazy-hydrate-target="host-id"
   * =================================
   *
   * Put this attribute on any control that lives OUTSIDE a <lazy-hydrate> host
   * but depends on content inside it. The canonical case is a tab strip: the
   * tab buttons are server-rendered above the fold for LCP and crawlability,
   * while the tab panels sit inside the host's <template>. Until hydration
   * those panels do not exist, so every tab's aria-controls points at nothing
   * and clicking a tab does nothing. The host's own focusin net cannot help,
   * because the tabs are not inside the host.
   *
   * WHY AN ATTRIBUTE AND NOT A WRAPPER ELEMENT
   * ------------------------------------------
   * The first version of this shipped as a <lazy-hydrate-trigger> wrapper.
   * That was wrong. Wrapping <tabs-nav> made it the first child of the new
   * element, which silently stopped `.tabs-nav:not(:first-child)` from
   * matching and dropped 32px of margin-top, collapsing the tabs against the
   * section heading.
   *
   * That failure generalises: any theme whose spacing leans on :first-child,
   * :last-child, :only-child, nth-child or sibling combinators will break when
   * you introduce a wrapper, and it breaks quietly - no console error, just
   * shifted layout. An attribute plus delegated listeners changes nothing
   * about the box tree or the sibling order, so there is nothing to break.
   *
   * Listeners are delegated from the document in the capture phase, so this
   * also works for markup injected after load. pointerover is used rather than
   * pointerenter because pointerenter does not bubble.
   *
   * All four events precede `click`, and hydrate() is synchronous, so the
   * panel is in the document before any tab handler reads it.
   */
  var TRIGGER_SELECTOR = '[data-lazy-hydrate-target]';
  var TRIGGER_EVENTS = ['pointerover', 'focusin', 'pointerdown', 'keydown'];

  function onTriggerIntent(event) {
    var node = event.target;
    if (!node || typeof node.closest !== 'function') return;

    var trigger = node.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    // Ignore bare modifier keypresses; any other signal counts as intent.
    if (event.type === 'keydown') {
      var k = event.key;
      if (k === 'Shift' || k === 'Control' || k === 'Alt' || k === 'Meta') return;
    }

    var id = trigger.getAttribute('data-lazy-hydrate-target');
    var host = id ? document.getElementById(id) : null;

    // Host not parsed yet. Leave the attribute in place so a later signal
    // still works rather than latching a permanent no-op.
    if (!host) return;

    trigger.removeAttribute('data-lazy-hydrate-target');

    if (typeof host.hydrate === 'function') {
      host.hydrate();
    } else {
      // Custom element has not upgraded yet. Retry once on the next tick; if
      // it still is not there, leave the DOM alone rather than half-cloning
      // the template ourselves.
      window.setTimeout(function () {
        if (typeof host.hydrate === 'function') host.hydrate();
      }, 0);
    }

    if (trigger.getAttribute('aria-busy') === 'true') {
      trigger.setAttribute('aria-busy', 'false');
    }
  }

  for (var i = 0; i < TRIGGER_EVENTS.length; i++) {
    document.addEventListener(TRIGGER_EVENTS[i], onTriggerIntent, true);
  }

})();